import { useState, useMemo, useRef } from "react";
import { Button, Typography, Flex } from "antd";
import {
  HeartOutlined,
  HeartFilled,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useFavourites } from "Features/Colleges/context/CollegeContext";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";
import { CollegePage } from "./CollegeInfo/CollegePage";
import { useUserInfo } from "GlobalStates/User";
import apiClient from "QueryClient/axiosClient";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";

const { Text } = Typography;

const PlaceholderLogo = ({ size = 58, image }) =>
  image ? (
    <div
      className="bg-gray-200 rounded-full flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <img
        src={`data:image/webp;base64,${image}`}
        alt="Logo"
        style={{
          width: size,
          height: size,
        }}
        className="rounded-lg"
      />
    </div>
  ) : (
    <div
      className="bg-gray-200 rounded-full flex-shrink-0 text-gray-500 flex items-center justify-center font-semibold"
      style={{ width: size, height: size }}
    >
      OG
    </div>
  );

const CollegeCard = ({
  college,
  onSave,
  showViewDetails = false,
  setSelectedCollege = null,
}) => {
  const [isSaved, setIsSaved] = useState(college.isSaved);
  const { user } = useUserInfo();

  const handleSaveClick = () => {
    setIsSaved((prev) => !prev);
    onSave(college.id, isSaved);
  };

  const tagColors = {
    Safe: "bg-emerald-50 text-emerald-700",
    Moderate: "bg-blue-50 text-blue-700",
    Ambitious: "bg-yellow-50 text-yellow-700",
  };

  const handleCollegeClick = async (id) => {
    try {
      ReactGA.send({ hitType: "pageview", page: `/colleges/${id}` });
      const response = await apiClient.get(`/universityDetails/${id}`);
      const collegeDetails = response.data;
      setSelectedCollege(collegeDetails);
    } catch (error) {
      toast.error("Failed to fetch college details");
    }
  };

  return (
    <div
      className="rounded-xl shadow-lg border-none overflow-hidden h-full flex flex-col justify-between align-center
                 transition-all duration-300 ease-in-out 
                 hover:-translate-y-2 
                 hover:shadow-xl p-4"
    >
      <Flex className="p-4 border-b border-gray-100 items-start justify-between">
        <Flex align="center" gap={10}>
          <PlaceholderLogo size={48} image={college.logo} />
          <Flex vertical>
            <p className="mb-1 text-slate-900 font-semibold leading-tight text-base text-left">
              {college.name}
            </p>
            <Text className="text-gray-500 text-base text-left">
              <span className="mr-1">📍</span>
              {college.location}
            </Text>
          </Flex>
        </Flex>
        {user?.isPremiumUser && (
          <Button
            type="text"
            shape="circle"
            icon={
              isSaved ? (
                <HeartFilled className="text-red-500" />
              ) : (
                <HeartOutlined className="text-gray-400 hover:text-red-500" />
              )
            }
            onClick={handleSaveClick}
            className="ml-auto"
          />
        )}
      </Flex>

      <Flex vertical className="p-4 flex-grow">
        <Flex gap={8} wrap="wrap" className="mb-4">
          {college.tags.map((tag) => (
            <span
              key={tag}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                tagColors[tag] || "bg-gray-100 text-gray-700"
              }`}
            >
              {tag}
            </span>
          ))}
        </Flex>
      </Flex>

      {showViewDetails && (
        <Flex className="border-t border-gray-100 w-full mt-auto">
          <Button
            type="primary"
            className="w-[80%] mx-auto font-semibold text-base hover:!bg-[#80e280] hover:!text-black hover:!shadow-lg hover:scale-105"
            onClick={() => {
              handleCollegeClick(college.id);
            }}
          >
            View Details
          </Button>
        </Flex>
      )}
    </div>
  );
};

const CollegeRecommendations = ({
  colleges,
  isPremiumUser,
  showViewDetails,
}) => {
  const [selectedCollege, setSelectedCollege] = useState(null);
  const navigate = useNavigate();
  const { addFavourite, removeFavourite } = useFavourites();
  const scrollRef = useRef(null);

  const safeColleges = useMemo(() => colleges?.safe || [], [colleges]);
  const moderateColleges = useMemo(() => colleges?.moderate || [], [colleges]);
  const ambitiousColleges = useMemo(
    () => colleges?.ambitious || [],
    [colleges]
  );

  const { limitedRecommendations, allRecommendations } = useMemo(() => {
    const buildCards = (list, tag) =>
      (list || []).map((college) => ({
        id: college.id,
        name: college.Name,
        location: college.City,
        logo: college.logo,
        tags: [tag],
        isSaved: college?.isFavourite,
      }));

    const safeCards = buildCards(safeColleges, "Safe");
    const moderateCards = buildCards(moderateColleges, "Moderate");
    const ambitiousCards = buildCards(ambitiousColleges, "Ambitious");

    const limited = [...safeCards, ...moderateCards, ...ambitiousCards];

    const all = [...safeCards, ...moderateCards, ...ambitiousCards];

    return { limitedRecommendations: limited, allRecommendations: all };
  }, [safeColleges, moderateColleges, ambitiousColleges]);

  // Premium users see all; non-premium see limited
  const collegeRecommendations = isPremiumUser
    ? allRecommendations
    : limitedRecommendations;

  const handleSaveCollege = (collegeId, isSaved) => {
    isSaved ? removeFavourite(collegeId) : addFavourite(collegeId);
  };

  const handleUnlockMore = () => navigate(ROUTES.PRICING);

  const shouldShowUnlockButton = !isPremiumUser;

  const handleArrowClick = (direction) => {
    const container = scrollRef.current;
    if (!container) return;

    const amount = container.offsetWidth * 0.7; // scroll ~70% of visible width
    container.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <div className="bg-white rounded-xl w-full flex flex-col items-center justify-center mt-6 p-2 mb-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-8 w-[90%] text-left">
        <div>
          <div className="mb-1 text-xl md:text-2xl font-bold text-slate-900 mb-2">
            AI-Powered Recommendations
          </div>
          <Text className="flex text-slate-600 text-base md:text-lg justify-left">
            Colleges perfectly matched to your profile
          </Text>
        </div>

        <div className="flex items-center gap-3">
          {shouldShowUnlockButton && (
            <Button
              type="primary"
              size="large"
              onClick={handleUnlockMore}
              className="text-sm md:text-base p-2 h-fit !bg-[#80e280] !text-black hover:!bg-[#80e280] hover:!text-black hover:!border-[#80e280] hover:scale-105"
            >
              View More
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable snap carousel with arrows */}
      <div className="w-full flex justify-center">
        <div className="relative w-[95%]">
          {/* Overlay arrows on sides (visible on md+) */}
          {collegeRecommendations.length > 0 && (
            <>
              <Button
                shape="circle"
                type="default"
                size="small"
                icon={<LeftOutlined />}
                onClick={() => handleArrowClick("left")}
                className="hidden md:flex items-center justify-center !absolute left-0 top-1/2 -translate-y-1/2 z-10 shadow-md"
              />
              <Button
                shape="circle"
                type="default"
                size="small"
                icon={<RightOutlined />}
                onClick={() => handleArrowClick("right")}
                className="hidden md:flex items-center justify-center !absolute right-0 top-1/2 -translate-y-1/2 z-10 shadow-md"
              />
            </>
          )}

          <div
            ref={scrollRef}
            className="w-full overflow-x-auto snap-x snap-mandatory flex gap-6 px-8 pb-4 no-scrollbar"
          >
            {collegeRecommendations.map((college, index) => (
              <div
                key={college.id}
                data-tour={index === 0 ? "college-card-1" : undefined}
                className="snap-center flex-shrink-0 w-[260px]"
              >
                <CollegeCard
                  college={{
                    ...college,
                    isSaved: college?.isSaved,
                  }}
                  onSave={handleSaveCollege}
                  showViewDetails={showViewDetails}
                  setSelectedCollege={setSelectedCollege}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedCollege && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="relative w-[90%] h-[90%] bg-white rounded-lg shadow-2xl overflow-y-auto p-6">
            <span
              role="button"
              tabIndex={0}
              aria-label="Close"
              onClick={() => setSelectedCollege(null)}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && setSelectedCollege(null)
              }
              className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl font-bold leading-none cursor-pointer select-none"
            >
              &times;
            </span>
            <CollegePage data={selectedCollege} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeRecommendations;
